/**
 * Minimal ERC-20 metadata reader (name / symbol / decimals) via JSON-RPC
 * eth_call against public RPC endpoints. No wallet or web3 library needed.
 * Used by the admin token settings to pull name & symbol straight from the
 * token contract.
 */

// selectors: name()=0x06fdde03, symbol()=0x95d89b41, decimals()=0x313ce567
export const ERC20_SELECTORS = {
  name: '0x06fdde03',
  symbol: '0x95d89b41',
  decimals: '0x313ce567',
};

export const RPC_URLS = {
  ethereum: 'https://cloudflare-eth.com',
  polygon: 'https://polygon-rpc.com',
  bsc: 'https://bsc-dataseed.binance.org',
  arbitrum: 'https://arb1.arbitrum.io/rpc',
  optimism: 'https://mainnet.optimism.org',
};

export function rpcUrlForNetwork(network) {
  return RPC_URLS[network] || RPC_URLS.ethereum;
}

export function isValidEvmAddress(address) {
  return /^0x[0-9a-fA-F]{40}$/.test(String(address || '').trim());
}

async function ethCall(network, to, data) {
  const res = await fetch(rpcUrlForNetwork(network), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'eth_call',
      params: [{ to, data }, 'latest'],
    }),
  });
  if (!res.ok) throw new Error(`RPC HTTP ${res.status}`);
  const json = await res.json();
  if (json.error) throw new Error(json.error.message || 'RPC error');
  return json.result;
}

/**
 * Decode an ABI-encoded return value. Handles both offsets-encoded strings
 * (standard) and bytes32-packed strings (old tokens like MKR/REP).
 */
export function decodeAbiString(hex) {
  if (!hex || hex === '0x') return '';
  const body = hex.replace(/^0x/, '');
  if (body.length >= 128) {
    const offset = parseInt(body.slice(0, 64), 16);
    if (offset === 32) {
      // dynamic string: [offset][length][data...]
      const len = parseInt(body.slice(64, 128), 16);
      const dataLen = Math.min(len * 2, body.length - 128);
      let str = '';
      for (let i = 0; i < dataLen; i += 2) {
        const code = parseInt(body.substr(128 + i, 2), 16);
        if (code === 0) break; // trailing zero padding
        str += String.fromCharCode(code);
      }
      return str;
    }
  }
  // bytes32 fallback: first slot, strip trailing zeros
  let str = '';
  for (let i = 0; i < 64; i += 2) {
    const code = parseInt(body.substr(i, 2), 16);
    if (code === 0) break;
    str += String.fromCharCode(code);
  }
  return str;
}

export function decodeAbiUint(hex) {
  if (!hex || hex === '0x') return NaN;
  const body = hex.replace(/^0x/, '');
  if (!body) return NaN;
  const first = body.slice(0, 64).replace(/^0+/, '');
  return parseInt(first || '0', 16);
}

/**
 * Fetch ERC-20 metadata (name, symbol, decimals) from the contract itself.
 * @param {string} address - token contract address
 * @param {string} network - one of RPC_URLS keys
 * @returns {Promise<{name: string, symbol: string, decimals: number}>}
 */
export async function fetchErc20Metadata(address, network) {
  const addr = String(address || '').trim();
  if (!isValidEvmAddress(addr)) {
    throw new Error('Invalid token contract address');
  }
  const [nameHex, symbolHex, decimalsHex] = await Promise.all([
    ethCall(network, addr, ERC20_SELECTORS.name),
    ethCall(network, addr, ERC20_SELECTORS.symbol),
    ethCall(network, addr, ERC20_SELECTORS.decimals),
  ]);
  const name = decodeAbiString(nameHex);
  const symbol = decodeAbiString(symbolHex);
  const decimals = decodeAbiUint(decimalsHex);
  return {
    name,
    symbol,
    decimals: isNaN(decimals) ? 0 : decimals,
  };
}
